import { MouseEvent, useEffect, useRef, useState } from "react"
import { GetApp } from "../../App"
import { Block, BlockField, BlockRow } from "../../Components/Block"
import { classNames } from "../../Components"
import { Icon, Icons } from "../../Components/Icon"
import { LoginSignInViewProps } from "./Params"

type LoginSignInFocusField = "email" | "password";

export function LoginSignInView({ active, onActivate, loginError, loading, showPassword, setShowPassword, submit, invitationData, setForgotPassword, onNativeSignIn }: LoginSignInViewProps) {
  const app = GetApp()!;
  const t = app.getT("LoginView");

  const emailRef = useRef<HTMLInputElement | null>(null);
  const passwordRef = useRef<HTMLInputElement | null>(null);

  // Tracks the email as the visitor types it, purely so the box's placeholder (shown
  // while the *other* box is active -- see LoginView, which only ever keeps one box's
  // real <form> mounted) still looks like the field it's mimicking instead of always
  // rendering blank, and so reactivating restores it into the real input. The password
  // is intentionally never captured here: unlike a partially-typed email, it has no
  // legitimate reason to be echoed back in a plain, unmasked <div>.
  //
  // `email` only ever holds what the visitor themselves typed (never seeded from
  // invitationData): invitationData is fetched asynchronously by LoginView (see the
  // ship-invitation flow), so it can arrive as a *later* prop update on this
  // already-mounted box. Deriving the displayed value at render time (falling back to
  // invitationData only while the visitor hasn't typed anything) means the input picks
  // up a late-arriving invitation email correctly, the same way the plain
  // `defaultValue={invitationData?.email}` expression it replaces did.
  const [email, setEmail] = useState("");
  const displayEmail = email || (invitationData && invitationData.email) || "";

  // Remembers which field (if any) the visitor clicked on this box's inactive
  // placeholder -- set by the field-specific handlers below -- so the effect below can
  // focus the corresponding real input once the click also activates this box.
  // `undefined` means the click landed elsewhere in the box (its background, a label,
  // the forgot-password link) rather than on a specific field's placeholder, so the
  // effect falls back to focusing the first empty field instead.
  const pendingFocusFieldRef = useRef<LoginSignInFocusField | undefined>(undefined);
  // Tracks whether this box was active on the previous render, so the focus-on-
  // activation effect below only fires on a real inactive -> active transition (a
  // visitor's click) -- never on initial mount, where this box may already start
  // active (e.g. the default login box), and autofocusing on page load with no user
  // interaction would be an unwanted UX regression.
  const wasActiveRef = useRef(active);

  useEffect(() => {
    const wasActive = wasActiveRef.current;
    wasActiveRef.current = active;
    if (!active || wasActive) {
      return;
    }
    const target = pendingFocusFieldRef.current;
    pendingFocusFieldRef.current = undefined;
    if (target === "password") {
      passwordRef.current?.focus();
    } else if (target === "email") {
      emailRef.current?.focus();
    } else if (displayEmail) {
      // A click elsewhere in the box (not on a specific field): focus the first empty
      // field. Email already has a value (typed earlier, or prefilled from an
      // invitation), so password -- always empty on a fresh activation, since it's
      // never mirrored into the placeholder -- is next.
      passwordRef.current?.focus();
    } else {
      emailRef.current?.focus();
    }
  }, [active, displayEmail]);

  const activateField = (field: LoginSignInFocusField) => (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    pendingFocusFieldRef.current = field;
    onActivate();
  };

  return (
    <div className={classNames(["login", !active && "inactive"])} onClick={active ? undefined : onActivate}>
      {app.cloudSession() && <h4 className="text_on_box">{t("login")}</h4>}

      <Block className="login-block">
        {/* Leads the box because it is the only path where Password AutoFill works from
            the app's localhost origin (see LoginView's `nativeSignInPossible`). The
            credential fields stay below the divider for the cases it cannot serve. */}
        {onNativeSignIn && (
          <div className="LoginView-native-sign-in-alt">
            <button type="button" className="button default LoginView-native-sign-in-fallback" disabled={loading} onClick={onNativeSignIn}>
              {t(loading ? "native_sign_in_loading" : "native_sign_in_fallback")}
            </button>
            <div className="LoginView-native-sign-in-or"><span>{t("native_sign_in_or")}</span></div>
          </div>
        )}
        <BlockRow><p>{t("login_email")}</p></BlockRow>
        {active ? (
          <form className="LoginView-form" onSubmit={submit}>
            {loginError && <div className="LoginView-error">{t(`error_${loginError}`)}</div>}
            {/* `username` / `current-password` are set unconditionally -- they used to be
                `off` for a non-cloud session, i.e. exactly the iOS/macOS shells, whose
                WKWebView serves this page from a fixed localhost origin. WebKit keys its
                Password AutoFill offer (the iOS QuickType key icon, the macOS inline
                suggestion list) off these tokens, so `off` suppressed it and left only
                the manual right-click -> AutoFill -> Passwords path. */}
            <BlockRow>
              <BlockField label={t("form_email")} labelFor={"login_email"}>
                <input ref={emailRef} id="login_email" name="email" type="email" autoComplete="username" defaultValue={displayEmail} onChange={(e) => setEmail(e.target.value)} />
              </BlockField>
            </BlockRow>

            <BlockRow>
              <BlockField label={t("form_password")} labelFor={"login_password"}>
                <span className="password-field">
                  <input ref={passwordRef} id="login_password" name="password" type={showPassword ? "text" : "password"} autoComplete="current-password" />
                  <Icon icon={showPassword ? Icons.EyeOn : Icons.EyeOff} className="password-toggle" onClick={() => setShowPassword(!showPassword)} />
                </span>
              </BlockField>
            </BlockRow>

            <BlockRow className="row-right login-actions">
              <div className="new-pw-button"><a href="#" onClick={() => { if (app.cloudSession()) { app.updateRoute("signup/forgot-password"); setForgotPassword(true); } else { app.openServer("signup/forgot-password"); } }}>{t("form_forgot_password")}</a></div>
              <button className="button submit-button" disabled={loading} type="submit">{t(loading ? "form_submit_login_loading" : "form_submit_login")}</button>
            </BlockRow>

          </form>
        ) : (
          // Inactive: no real <form>/<input> -- just a look-alike placeholder (see
          // `.input` in Common.scss) so this box can never contribute a second, real
          // email/password field to the DOM alongside the signup box's. Each
          // placeholder still calls `onActivate` (via `activateField`) when clicked, so
          // clicking anywhere in the box still activates it -- but stops the click from
          // bubbling to the outer box's generic handler, so the specific field that was
          // clicked (not just "the first empty field") gets focused once active.
          <div className="LoginView-form">
            <BlockRow>
              <BlockField label={t("form_email")}>
                <div className="input" onClick={activateField("email")}>{displayEmail}</div>
              </BlockField>
            </BlockRow>

            <BlockRow>
              <BlockField label={t("form_password")}>
                <span className="password-field">
                  <div className="input" onClick={activateField("password")} />
                </span>
              </BlockField>
            </BlockRow>

            <BlockRow className="row-right login-actions">
              <div className="new-pw-button"><span>{t("form_forgot_password")}</span></div>
              {/* A real (disabled-when-loading) <button>, not a plain <div> like the
                  other placeholders: browser UA styling only renders the "disabled"
                  look (dimmed background/border) on an actual disabled form control,
                  matching what this box's real button would show if a submission is
                  still in flight when the visitor switches away. No click handler
                  needed since `type="button"` plus the outer box's own onClick already
                  covers "click anywhere to activate". */}
              <button className="button submit-button" disabled={loading} type="button">{t(loading ? "form_submit_login_loading" : "form_submit_login")}</button>
            </BlockRow>
          </div>
        )}
      </Block>
    </div>
  );
}
